import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { companyService } from '../services/companyService';
import { trackingHelpers } from '@/services/mixpanel';
import { useAuthStore } from '@features/auth/store/authStore';
import { getDefaultRouteForRole } from '@lib/utils/roleNavigation';

interface UseCompaniesOptions {
  /** Whether to navigate to dashboard after selecting a company. Defaults to true. */
  navigateOnSelect?: boolean;
}

/**
 * Hook for managing companies
 */
export function useCompanies(options: UseCompaniesOptions = {}) {
  const { navigateOnSelect = true } = options;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Get companies
  const {
    data: companiesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['companies'],
    queryFn: companyService.getCompanies,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get selected company
  const { data: selectedCompanyData, refetch: refetchSelectedCompany, isLoading: isLoadingSelectedCompany, isFetching: isFetchingSelectedCompany } = useQuery({
    queryKey: ['company', 'selected'],
    queryFn: companyService.getSelectedCompany,
    staleTime: 30 * 1000, // 30 seconds - shorter to ensure fresher data
    refetchOnMount: 'always', // Always refetch when component mounts
    retry: 2, // Retry twice if it fails
  });

  // Select company mutation
  const selectCompanyMutation = useMutation({
    mutationFn: (companyId: string) => companyService.selectCompany(companyId),
    onSuccess: async (_data, companyId) => {
      console.log('🔄 Company selected, clearing and invalidating all queries...');

      // Get company info for tracking
      const newCompany = companiesData?.companies.find(c => c.id === companyId);
      const previousCompanyId = selectedCompanyData?.company?.id || '';

      // Track company switch
      if (newCompany) {
        trackingHelpers.trackCompanySwitch(
          companyId,
          newCompany.name,
          previousCompanyId
        );
      }

      // CLEAR all company-specific data from cache completely
      // This ensures no stale data from previous company
      queryClient.removeQueries({ queryKey: ['invoices'] });
      queryClient.removeQueries({ queryKey: ['customers'] });
      queryClient.removeQueries({ queryKey: ['products'] });
      queryClient.removeQueries({ queryKey: ['certificates'] });
      queryClient.removeQueries({ queryKey: ['templates'] });
      queryClient.removeQueries({ queryKey: ['template'] }); // singular template query
      queryClient.removeQueries({ queryKey: ['branchOffices'] });
      queryClient.removeQueries({ queryKey: ['branch-offices'] }); // alternative key used in useUsers
      queryClient.removeQueries({ queryKey: ['company', 'settings'] });
      queryClient.removeQueries({ queryKey: ['dashboard'] });
      queryClient.removeQueries({ queryKey: ['subscription'] }); // All subscription queries (['subscription', 'current'], ['subscription', 'available-plans'], etc.)
      queryClient.removeQueries({ queryKey: ['subscription-verification'] });
      queryClient.removeQueries({ queryKey: ['consumption'] }); // Plan consumption data
      queryClient.removeQueries({ queryKey: ['users'] }); // company users
      queryClient.removeQueries({ queryKey: ['user'] }); // individual user queries
      queryClient.removeQueries({ queryKey: ['roles'] }); // company roles

      // Update the selected company info immediately
      await queryClient.invalidateQueries({
        queryKey: ['company', 'selected'],
        refetchType: 'active' // Refetch this one immediately
      });
      await queryClient.invalidateQueries({
        queryKey: ['companies'],
        refetchType: 'active'
      });

      console.log('✅ All company-specific queries cleared and core queries invalidated');

      // Navigate based on user role only if requested
      if (navigateOnSelect) {
        const defaultRoute = getDefaultRouteForRole(user?.role);
        navigate(defaultRoute);
      }
    },
    onError: (error: Error) => {
      console.error('Failed to select company:', error);
    },
  });

  return {
    companies: companiesData?.companies || [],
    companiesCount: companiesData?.count || 0,
    selectedCompany: selectedCompanyData?.company || null,
    isLoading,
    isLoadingSelectedCompany: isLoadingSelectedCompany || isFetchingSelectedCompany,
    error,
    refetch,
    refetchSelectedCompany,
    selectCompany: (companyId: string) => selectCompanyMutation.mutate(companyId),
    isSelecting: selectCompanyMutation.isPending,
  };
}
